import { Component, OnInit, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { BlockedProfessional } from '../../../../core/models/professional.model';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RoutingAwareDialogService } from '../../../../core/services/routing-aware-dialog.service';
import { BlockScheduleModalComponent } from '../../../schedule/components/block-schedule-modal/block-schedule-modal.component';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';

@Component({
    selector: 'app-absence-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        AppPrimeTable,
        AppButton,
        InputTextModule,
        DatePickerModule,
        IconFieldModule,
        InputIconModule,
        ConfirmDialogModule,
        TooltipModule,
        ToastModule,
        HasPermissionDirective
    ],
    providers: [ConfirmationService, { provide: DialogService, useClass: RoutingAwareDialogService }, MessageService],
    templateUrl: './absence-list.html',
})
export class AbsenceList implements OnInit {
    blockedProfessionals: BlockedProfessional[] = [];
    loading = true;
    ref: DynamicDialogRef | undefined;
    branchId: number | null = null;

    // Pagination
    currentPage = 1;
    pageSize = 10;
    totalRecords = 0;
    searchText = '';

    // Date filters - Using Date objects for PrimeNG DatePicker
    startDate: Date | null = null;
    endDate: Date | null = null;

    cols: PrimeTableColumn[] = [
        { field: 'doctor_name', header: 'professionals_doctor_name', type: 'custom' },
                { field: 'block_count', header: 'bchmedk2', type: 'custom' },

        { field: 'last_block_start_date', header: 'professionals_date_start', type: 'custom' },
        { field: 'last_block_end_date', header: 'professionals_date_end', type: 'custom' },
        { field: 'last_start_block_time', header: 'professionals_time_start', type: 'custom' },
        { field: 'last_end_block_time', header: 'professionals_time_end', type: 'custom' },
        { field: 'last_block_reason', header: 'professionals_reason' },
        { field: 'actions', header: 'actions', type: 'custom', width: '50px' }
    ];

    @ViewChild('doctor_name') doctorNameTemplate!: TemplateRef<any>;
    @ViewChild('date_cell') dateCellTemplate!: TemplateRef<any>;
    @ViewChild('time_cell') timeCellTemplate!: TemplateRef<any>;
    @ViewChild('actions_cell') actionsCellTemplate!: TemplateRef<any>;

    templates: { [key: string]: TemplateRef<any> } = {};

    constructor(
        private professionalsService: ProfessionalsService,
        private cdr: ChangeDetectorRef,
        private confirmationService: ConfirmationService,
        private dialogService: DialogService,
        private store: Store,
        private messageService: MessageService,
        private translateSvc: TranslateService
    ) { }

    ngOnInit() {
        this.store.select(selectBranchId).subscribe(branchId => {
            if (branchId) {
                this.branchId = branchId;
            } else {
                this.loadBranches();
            }
        });
        
        this.loadBlockedProfessionals();
    }

    async loadBranches() {
        // Fallback if not in store
        const { data } = await this.professionalsService.getBranches();
        if (data && data.length > 0) {
            this.branchId = data[0].id;
        }
    }

    ngAfterViewInit() {
        this.templates = {
            'doctor_name': this.doctorNameTemplate,
            'last_block_start_date': this.dateCellTemplate,
            'last_block_end_date': this.dateCellTemplate,
            'last_start_block_time': this.timeCellTemplate,
            'last_end_block_time': this.timeCellTemplate,
            'actions': this.actionsCellTemplate
        };
        this.cdr.detectChanges();
    }

    async loadBlockedProfessionals() {
        this.loading = true;
        this.cdr.detectChanges();

        const offset = (this.currentPage - 1) * this.pageSize;

        try {
            // Format dates back to string for API if needed, but getBlockedProfessionals seems to expect strings
            const startStr = this.startDate ? this.startDate.toISOString().split('T')[0] : '';
            const endStr = this.endDate ? this.endDate.toISOString().split('T')[0] : '';

            const { data, error } = await this.professionalsService.getBlockedProfessionals(
                this.pageSize,
                offset,
                startStr,
                endStr,
                this.searchText
            );

            if (data) {
                this.blockedProfessionals = data.doctors || [];
                // It seems total_pages * pageSize is a rough estimate, or we can use totalCount if service returned it.
                // For now, let's keep it consistent with the existing total_pages logic but adapt to totalRecords if possible.
                this.totalRecords = (data.total_pages || 0) * this.pageSize;
            } else {
                console.error('Error fetching blocked professionals', error);
                this.blockedProfessionals = [];
                this.totalRecords = 0;
            }
        } catch (e) {
            console.error('Exception fetching blocked professionals', e);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    onSearch() {
        if (this.searchText.length === 0 || this.searchText.length >= 3) {
            this.currentPage = 1;
            this.loadBlockedProfessionals();
        }
    }

    onDateChange() {
        this.currentPage = 1;
        this.loadBlockedProfessionals();
    }

    onPageChange(event: any) {
        this.currentPage = (event.first / event.rows) + 1;
        this.pageSize = event.rows;
        this.loadBlockedProfessionals();
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    }

    formatTime(timeString: string): string {
        if (!timeString) return '-';
        return timeString.substring(0, 5); // HH:MM format
    }

    clearFilters() {
        this.searchText = '';
        this.startDate = null;
        this.endDate = null;
        this.currentPage = 1;
        this.loadBlockedProfessionals();
    }

    onDelete(row: BlockedProfessional) {
        this.confirmationService.confirm({
            message: this.translateSvc.instant('professionals_confirm_remove_block'),
            header: this.translateSvc.instant('professionals_remove_block_header'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translateSvc.instant('common_yes'),
            rejectLabel: this.translateSvc.instant('common_no'),
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: async () => {
                if (row.last_blockage_id) {
                     const { error } = await this.professionalsService.deleteBlockage(row.last_blockage_id);
                     if (error) {
                         console.error('Error deleting block', error);
                         this.messageService.add({severity: 'error', summary: this.translateSvc.instant('error'), detail: this.translateSvc.instant('professionals_block_remove_failed')});
                     } else {
                         this.messageService.add({severity: 'success', summary: this.translateSvc.instant('success'), detail: this.translateSvc.instant('professionals_block_removed')});
                         this.loadBlockedProfessionals();
                     }
                }
            }
        });
    }

    openBlockSchedule() {
        if (!this.branchId) {
            console.warn('No branch ID available');
            return;
        }

        const ref = this.dialogService.open(BlockScheduleModalComponent, {
            showHeader: false, // Use internal component header
            width: '500px',
            contentStyle: { overflow: 'visible', padding: '0' }, // Reset padding for custom component
            baseZIndex: 10000,
            data: {
                branchId: this.branchId
            }
        });
        
        if (ref) {
            this.ref = ref;
            ref.onClose.subscribe((result) => {
                if (result) {
                    this.loadBlockedProfessionals();
                }
            });
        }
    }

}
